import { z } from 'zod'

/**
 * Runtime configuration, stored as key/value rows in D1 (`config` table) and
 * cached in the Worker. Never hardcoded — see docs/decisions/0001.
 */
export const appConfigSchema = z.object({
  llmModelFast: z.string().default('claude-sonnet-5'),
  llmModelStrong: z.string().default('claude-opus-5'),
  /**
   * Leer lassen heißt: gar nicht mitschicken. Das ist der Normalfall — die
   * aktuellen Claude-Modelle lehnen `temperature` ausdrücklich ab ("deprecated
   * for this model") und brechen die Anfrage mit 400 ab. Der Wert bleibt
   * trotzdem einstellbar, weil ältere Modelle ihn annehmen und die Wahl des
   * Modells in der Konfiguration steht, nicht im Code.
   */
  llmTemperature: z.number().min(0).max(1).nullable().default(null),
  /**
   * Nachdenken vor der Antwort (Anthropic „extended thinking"). `off` ist der
   * Normalfall: Claude Sonnet 5 und Opus 5 denken von sich aus, wenn man
   * nichts sagt — und jede Sekunde davon liegt vor dem ersten Wort, im
   * Sprachmodus als Stille. Für den Dialog im Lab braucht es das nicht.
   * `adaptive` lässt das Modell selbst entscheiden (zum Vergleichen). Wie
   * der Wert je Modell abgebildet wird, steht im Anthropic-Provider.
   */
  llmThinking: z.enum(['off', 'adaptive']).default('off'),
  voiceId: z.string().default(''),
  voiceProvider: z.enum(['mock', 'elevenlabs-agents', 'elevenlabs-pipeline']).default('mock'),
  sttProvider: z.enum(['mock', 'elevenlabs']).default('mock'),
  embeddingProvider: z.enum(['mock', 'workers-ai']).default('mock'),
  llmProvider: z.enum(['mock', 'anthropic']).default('mock'),
  visionProvider: z.enum(['mock', 'workers-ai']).default('mock'),
  /** cosine-similarity threshold above which a showcase counts as recognized (FR-31) */
  showcaseRecognitionThreshold: z.number().min(0).max(1).default(0.82),
  /** number of consecutive stable frames required before announcing recognition */
  showcaseRecognitionStableFrames: z.number().int().min(1).default(3),
  webSearchEnabled: z.boolean().default(false),
  /**
   * Wissenssuche **vor** der ersten Modellrunde, nicht erst als Werkzeug in
   * der zweiten. Ohne das braucht jede Antwort zwei volle Modellrunden (erst
   * „ich suche", dann die Antwort) — im Sprachmodus waren das vier bis acht
   * Sekunden bis zum ersten Wort, und ElevenLabs bricht nach etwa vier ab.
   * Abschaltbar zum Vergleichen; das Werkzeug bleibt dem Modell in beiden
   * Fällen für gezielte Nachfragen.
   */
  ragPrefetch: z.boolean().default(true),
  /**
   * Nach so vielen Stunden ohne Nachricht beginnt beim nächsten Öffnen ein
   * neues Gespräch (Flo, 22.10.: „wenn ich zwei Tage nicht mit K4ia arbeite,
   * habe ich meine alten Chats wieder“). Das alte bleibt in der Datenbank
   * (Analytics, Löschfrist wie bisher), nur der Chat fängt leer an. 0 = nie.
   * Gilt für den Chat am Handy (Web); Bühne und Kiosk räumen selbst auf.
   */
  chatTimeoutHours: z.number().min(0).max(168).default(4),
  /**
   * Ab so vielen Minuten Pause seit der letzten Nachricht fragt der Chat beim
   * Wiederkommen „weitermachen oder neu anfangen?“ (solange das Gespräch
   * noch nicht abgelaufen ist). 0 = bei jedem Öffnen. Wer nur kurz die App
   * wechselt, soll nicht jedes Mal gefragt werden.
   */
  chatResumeHintMinutes: z.number().int().min(0).max(1440).default(15),
  /**
   * Mindestähnlichkeit (Kosinus, 0–1), ab der ein Treffer der Vorab-Suche
   * dem Modell gezeigt und als Quelle genannt wird. Ohne Schwelle bekam eine
   * Bildfrage („Was siehst du?") die fünf nächstbesten Absätze irgendeines
   * Showcases — und K4ia nannte sie als Quelle und kommentierte sie sogar.
   * Der Wert hängt am Einbettungsmodell (bge-m3) und ist zum Nachstellen da.
   */
  ragMinScore: z.number().min(0).max(1).default(0.5),
  /**
   * Was K4ia im Sprachmodus tut, wenn die Kamera einen Showcase sicher erkennt
   * und der Besucher gerade nicht spricht: schweigen (`off`), in einem Satz
   * sagen, wo er steht, und anbieten (`offer`), oder gleich kurz erklären
   * (`explain`). Die SPEC sah ursprünglich nur das Angebot vor („nie
   * ungefragtes Losreden"); der Auftraggeber hat für das Lab das Erklären
   * gewählt — im Sprachmodus steht jemand vor dem Stand und wartet.
   */
  cameraVoiceAnnounce: z.enum(['off', 'offer', 'explain']).default('explain'),
  /**
   * Überbrückungswort im Sprachmodus („buffer words" in der ElevenLabs-
   * Dokumentation): Kommt nach `voiceFillerAfterMs` noch kein Inhalt vom
   * Modell, spricht K4ia dieses Wort, damit ElevenLabs die Anfrage nicht als
   * hängend abbricht und neu stellt. Leer heißt aus.
   */
  voiceFillerText: z.string().default('Hm,'),
  voiceFillerAfterMs: z.number().int().min(200).max(10000).default(1500),
  costLimitPerSessionEur: z.number().positive().default(2.0),
  costLimitPerDayEur: z.number().positive().default(150),
  /**
   * Preise je eine Million Token, in Euro — bewusst konfigurierbar und nicht
   * im Code: Anbieterpreise ändern sich, und eine Kostengrenze, die mit
   * veralteten Preisen rechnet, schützt vor der falschen Zahl.
   *
   * Die Vorgaben sind eine grobe Einordnung und müssen vor dem produktiven
   * Betrieb gegen die aktuelle Preisliste des Anbieters gesetzt werden; welcher
   * Wert gilt, hängt am gewählten Modell (`llmModelFast`) und am Vertrag.
   * Zwischengespeicherte Eingabe-Token kosten deutlich weniger und haben
   * deshalb einen eigenen Wert.
   */
  llmPriceInputPerMTokenEur: z.number().nonnegative().default(3.0),
  llmPriceOutputPerMTokenEur: z.number().nonnegative().default(15.0),
  llmPriceCachedInputPerMTokenEur: z.number().nonnegative().default(0.3),
  /**
   * Grundwissen, das in **jedem** Prompt steht: Wer J4F ist, wo das Lab ist,
   * wie man jemanden erreicht — die Dinge, die K4ia nie nachschlagen soll,
   * weil jede zweite Frage sie streift. Gepflegt im Admin unter „Wissen".
   * Kurz halten (KNOWLEDGE_FACTS_MAX_CHARS, geprüft beim Speichern im Admin):
   * alles Lange gehört in die Wissensbasis, wo die Suche es gezielt holt. Hier
   * bewusst ohne Obergrenze — ein zu langer Wert soll die Speicherung
   * ablehnen, nicht das Laden der ganzen Konfiguration scheitern lassen.
   */
  knowledgeFacts: z.string().default(''),
  /**
   * Teams-Kanal für Meldungen (FR-43): „Mitarbeiter rufen“ und neue
   * Wissenslücken. Gewählt im Admin aus den Kanälen, in die K4ia eingeladen
   * wurde (Tabelle teams_channels); leer heißt: Vars aus der Umgebung, sonst
   * Mock. Die Service-URL gehört zum Kanal und wird mit ausgewählt.
   */
  teamsStaffConversationId: z.string().default(''),
  teamsStaffServiceUrl: z.string().default(''),
  /** Neue Wissenslücken als Meldung in den Teams-Kanal. */
  teamsGapNotify: z.boolean().default(true),
  /** Ansprechpartner eines Showcases per Teams-Direktnachricht informieren, wenn eine Rückfrage entsteht. */
  teamsContactNotify: z.boolean().default(true),
  /** Besucher mit Mailadresse bekommen nach einer Rückfrage eine Eingangsbestätigung. */
  followupConfirmMail: z.boolean().default(true),
  /**
   * Live-Wand (`/wand`): die letzten Besucherfragen zeigen? Ab Werk aus — die
   * Wand steht im Raum. Auch eingeschaltet kommen nur kurze Fragen ohne
   * Mailadresse, Nummer oder Namen durch (workers/api/src/db/wand-repo.ts).
   */
  wandFragenZeigen: z.boolean().default(false),
  /**
   * Telefon (docs/TELEFON-EINRICHTUNG.md): Will ein Besucher im Lab mit einem
   * Menschen sprechen (`call_staff`), ruft K4ia zusätzlich zur Teams-Meldung
   * die Rufbereitschaft an — über ElevenLabs und eine Twilio-Nummer.
   */
  telefonAnrufen: z.boolean().default(false),
  /**
   * Wer angerufen wird, eine Person je Zeile: „Name: +49 170 1234567“. Die
   * erste Zeile zuerst. Dieselben Nummern erkennt K4ia, wenn sie anrufen, und
   * grüßt mit Namen.
   */
  telefonRufbereitschaft: z.string().default(''),
  /**
   * Wann K4ia anruft, z. B. „Mo-Fr 09:00-18:00“ (Zeit in Berlin), mehrere
   * Fenster mit Komma. Leer heißt: immer. Außerhalb bleibt es bei der
   * Teams-Meldung.
   */
  telefonZeiten: z.string().default('Mo-Fr 09:00-18:00'),
  /** Man kann K4ia unter der Nummer anrufen und mit ihr sprechen wie im Sprachmodus. */
  telefonEingehend: z.boolean().default(true),
  /**
   * Nur Nummern aus der Rufbereitschaft dürfen mit K4ia telefonieren. Schützt
   * vor Kosten durch Fremde, die die Nummer wählen.
   */
  telefonNurBekannte: z.boolean().default(false),
  /**
   * Was K4ia sagt, wenn jemand anruft. `{name}` wird durch den Namen ersetzt,
   * wenn die Nummer bekannt ist. Dass sie eine KI ist, gehört in den ersten
   * Satz — am Telefon sieht man es nicht.
   */
  telefonBegruessung: z
    .string()
    .default(
      'Hallo{name}, hier ist K4ia, die KI-Assistentin des J4F VisionLab. Was kann ich für dich tun?',
    ),
  /**
   * Stichwörter, die K4ia im Sprachmodus sofort unterbrechen — ohne die
   * Sekunde Sprache, die sonst nötig ist (stichwort.ts). Eins je Zeile oder
   * durch Komma getrennt; leer heißt: die Vorgabe.
   */
  dazwischenWoerter: z.string().default(''),
  /**
   * Was K4ia auf der Seite `/intro` spricht, sobald jemand nach dem Scan des
   * Intro-Tags am Handy „Intro starten" tippt. Gesprochen über die ElevenLabs-API mit der Stimme des Agenten;
   * die fertige Tonspur liegt danach in R2, bis der Text sich ändert
   * (routes/intro.ts). Gepflegt im Admin unter „Konfiguration".
   */
  introText: z
    .string()
    .default(
      'Hallo, ich bin K4ia, die KI-Assistentin des J4F VisionLab. Schön, dass ihr da seid! Hier zeigen wir, wie Fashion und Technologie zusammenwachsen. Sprecht mich einfach an — ich erzähle euch gern mehr.',
    ),
  /**
   * Was K4ia im Schaufenster (`/schaufenster`) sagt, sobald jemand den
   * QR-Code scannt und damit die Steuerung übernimmt. Wie `introText`, nur
   * für Leute auf der Straße — kurz, und mit dem Hinweis, was sie am Handy
   * tun können.
   */
  schaufensterText: z
    .string()
    .default(
      'Hallo, ich bin K4ia vom J4F VisionLab! Schön, dass du stehen bleibst. Tipp auf deinem Handy auf eine Box, und ich erzähle dir davon — oder frag mich einfach etwas.',
    ),
  /**
   * Nach dem Scan eines Showcase-Tags fragt K4ia „Vorlesen“ oder „Im Chat
   * lesen“. Aus: nur „Im Chat lesen“ — etwa wenn es am Stand zu laut ist oder
   * niemand mit dem Handy laut sprechen soll. Der Sprachmodus im Chat bleibt
   * davon unberührt.
   */
  tagVorlesen: z.boolean().default(true),
  conversationRetentionDays: z.number().int().positive().default(30),
  defaultAddressForm: z.enum(['du', 'sie']).default('du'),
  systemPromptVersion: z.string().default('v1'),
})
export type AppConfig = z.infer<typeof appConfigSchema>

export const configEntrySchema = z.object({
  key: z.string(),
  value: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string().optional(),
})
export type ConfigEntry = z.infer<typeof configEntrySchema>
